import React, { useState } from 'react';
import { MaterialReactTable, type MRT_ColumnDef } from 'material-react-table';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, Box, CircularProgress, useTheme, Skeleton, styled } from "@mui/material";
import { useGetAllIssuesListingQuery } from '../../../store/slices/apis/issuesDashboardSlice';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';

const StyledTableHead = styled(TableCell)({
  fontWeight: 500,
  fontSize: "14px",
  color: "#505258",
  padding: "10px 14px",
  backgroundColor: "#F8F8F8",
  border: "1px solid #D8D8D8",
  textAlign: "left",
  whiteSpace: "nowrap",
});

// Styled Table Body Cell
const StyledTableBody = styled(TableCell)({
  fontWeight: 400,
  fontSize: "14px",
  color: "#5052588A",
  padding: "10px 14px",
  backgroundColor: "#FFFFFF",
  border: "1px solid #D8D8D8",
  textAlign: "left",
  whiteSpace: "normal",
});

type Person = {
  //sno: string;
  issueDescription: string;
  package: string;
  contractor: string;
  reportedDate: string;
  // workflowStatus: string;
  currentStatus: string;
  timeImpact: string;
  costImpact: string;
};

const columns: MRT_ColumnDef<Person>[] = [
  // { accessorKey: 'sno', header: 'S No.' },
  { accessorKey: 'issueDescription', header: 'Issue Description', muiTableHeadCellProps: { align: 'center' }, muiTableBodyCellProps: { align: 'center', sx: { whiteSpace: 'nowrap' }, }, },
  { accessorKey: 'package', header: 'Package', muiTableHeadCellProps: { align: 'center' }, muiTableBodyCellProps: { align: 'center' }, },
  { accessorKey: 'contractor', header: 'Consultant/Contractor', muiTableHeadCellProps: { align: 'center' }, muiTableBodyCellProps: { align: 'center', sx: { whiteSpace: 'nowrap' }, }, },
  { accessorKey: 'reportedDate', header: 'Reported Date', muiTableHeadCellProps: { align: 'center' }, muiTableBodyCellProps: { align: 'center' }, },
  { accessorKey: 'currentStatus', header: 'Current Status', muiTableHeadCellProps: { align: 'center' }, muiTableBodyCellProps: { align: 'center' }, },
  { accessorKey: 'timeImpact', header: 'Time Impact', muiTableHeadCellProps: { align: 'center' }, muiTableBodyCellProps: { align: 'center' }, },
  { accessorKey: 'costImpact', header: 'Cost Impact', muiTableHeadCellProps: { align: 'center' }, muiTableBodyCellProps: { align: 'center' }, },
];

// Format helper
const formatDate = (dateString?: string) =>
  dateString ? dayjs(dateString).format('DD/MM/YY') : '';

// Flatten formatter (no children)
const formatScheduleData = (data: any[]): Person[] =>
  data.map((item) => ({
    // sno: item.sno,
    issueDescription: item.issueDescription,
    package: item.package,
    contractor: item.contractor,
    reportedDate: formatDate(item.reportedDate),
    currentStatus: item.currentStatus,
    timeImpact: item.timeImpact,
    costImpact: `₹${item.costImpact}`,
  }));

export default function IssuesList() {

  const newtheme = useTheme();
  const { projectId } = useParams();

  const { data: AllIssuesListing = [], isLoading, error } = useGetAllIssuesListingQuery({
    projectId: projectId,
  });

  const dataval: Person[] = AllIssuesListing?.data?.issues ? formatScheduleData(AllIssuesListing?.data?.issues) : [];

  if (isLoading) {
    return (
      <Paper sx={{ boxShadow: 'none', paddingBottom: '10px', minHeight: '300px' }}>
        <Skeleton variant="text" width={150} height={30} sx={{ mt: 2, mb: 2, ml: 3 }} />

        <TableContainer sx={{ padding: '10px' }}>
          <Table sx={{ border: '1px solid #E9E9E7', borderRadius: '4px' }}>
            <TableHead>
              <TableRow>
                {[...Array(9)].map((_, idx) => (
                  <StyledTableHead key={idx}>
                    <Skeleton variant="text" width={100} height={16} />
                  </StyledTableHead>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {[...Array(5)].map((_, rowIdx) => (
                <TableRow key={rowIdx}>
                  {[...Array(9)].map((_, cellIdx) => (
                    <StyledTableBody key={cellIdx}>
                      <Skeleton variant="text" width="100%" height={20} />
                    </StyledTableBody>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  }

  return (
    <Box sx={{
      width: "100%", border: "1px solid #D8D8D8", mt: "10px", borderRadius: "12px", overflow: "hidden",
    }}>
      <Box sx={{ backgroundColor: "#fff", p: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 500, color: `${newtheme?.customSettings?.themeColor}`, fontSize: "18px" }}>
          Issue Table
        </Typography>
      </Box>
      {isLoading ? (
        <Box display="flex" justifyContent="center" mt={4} pb={1}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error">Failed to load data.</Typography>
      ) : (
        <MaterialReactTable
          columns={columns}
          data={dataval}
          enableRowNumbers
          rowNumberDisplayMode="static"
          displayColumnDefOptions={{
            'mrt-row-numbers': {
              header: 'S No',
              size: 100,
              muiTableHeadCellProps: { align: 'center' },
              muiTableBodyCellProps: { align: 'center' },
            },
          }}
          muiTableHeadCellProps={{
            sx: {
              fontWeight: 500,
              fontSize: "14px",
              color: "#505258",
              padding: "10px 14px",
              backgroundColor: "#F8F8F8",
              border: "1px solid #D8D8D8",
            },
          }}
          muiTableBodyRowProps={{
            sx: {
              fontWeight: 400,
              fontSize: "14px",
              color: "#505258",
              padding: "10px 14px",
              backgroundColor: "#FFFFFF",
              border: "1px solid #D8D8D8",
              opacity: "0.54"
            },
          }}
          enableExpanding={false}
          enableRowSelection={false}
          enableFullScreenToggle={false}
          enableColumnActions={false}
        />
      )}
    </Box>
  );
}